import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Send } from "lucide-react";
import { toast } from "sonner";

export default function PortalMessages() {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef();

  const load = async () => {
    try { const { data } = await api.get("/portal/messages"); setMsgs(data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post("/portal/messages", { body });
      setBody("");
      await load();
    } catch (e) { toast.error("Could not send message"); }
    finally { setSending(false); }
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-[#252221] mb-1">Messages</h2>
      <p className="text-sm text-muted-foreground mb-5">Stay in touch with your MovingMyMama coordinator.</p>
      <Card className="border-[#E6EDF2] overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="h-[420px] overflow-y-auto mmm-scroll p-4 space-y-3 bg-[#F7FAFC]">
          {loading ? <Skeleton className="h-16 w-2/3" /> : msgs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">No messages yet. Say hello to your coordinator!</p>
          ) : msgs.map((m) => {
            const mine = m.from_portal;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="portal-message">
                <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${mine ? "text-white" : "bg-white border border-[#E6EDF2]"}`}
                  style={mine ? { backgroundColor: "var(--mmm-navy)" } : {}}>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                  <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>{m.author_name} · {formatDateTime(m.created_at)}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-[#E6EDF2] bg-white flex gap-2 items-end">
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={1} placeholder="Type a message..." data-testid="portal-message-input" className="resize-none" />
          <Button onClick={send} disabled={sending || !body.trim()} data-testid="portal-message-send" className="text-white shrink-0" style={{ backgroundColor: "var(--mmm-red)" }}>
            <Send size={16} />
          </Button>
        </div>
      </Card>
    </div>
  );
}
